import React, { Context, createContext, PureComponent, ReactNode } from 'react';
import uuid from 'uuid/v4';
import { BzitVehicleDetails } from '../../bzit';
import { reportError } from '../../error-handling/reportError';
import { withErrorBoundary } from '../../error-handling/withErrorBoundary';
import { add, listDetails, remove } from '../../helper/fetch/BookmarkService';
import offlineStore from '../../store/offlineStore';
import { Maybe } from '../../types';
import { getVehicleDetails } from '../SearchProvider/fetcher';
import { addedVehicleToWatchlist } from '../TrackingProvider/conversions/addedVehicleToWatchlist';
import { withTrackingApi, WithTrackingApiProps } from '../TrackingProvider/withTrackingApi';
import { withTypo3, WithTypo3Props } from '../Typo3Provider/withTypo3';

const BOOKMARKSTORE_KEY = 'bookmarkStore';

export interface BookmarkApi {
  add: (vehicleId: string) => Promise<void>;
  remove: (vehicleId: string) => Promise<void>;
  bookmarks: BzitVehicleDetails[];
  userId: string;
}

interface BookmarkStore {
  userId: string;
  bookmarks: BzitVehicleDetails[];
}

type BookmarkProviderState = {
  initialized: boolean;
} & BookmarkApi;

interface BookmarkProviderProps {
  children: ReactNode;
}

type BookmarkContextType = Context<BookmarkApi>;

const defaultContextValue: BookmarkApi = {
  add: async () => Promise.resolve(),
  remove: async () => Promise.resolve(),
  bookmarks: [],
  userId: ''
};

export const BookmarkContext: BookmarkContextType = createContext(
  defaultContextValue
);

const generateStore = (): BookmarkStore => ({
  userId: uuid(),
  bookmarks: []
});

class BookmarkProvider extends PureComponent<
  BookmarkProviderProps & WithTrackingApiProps & WithTypo3Props,
  BookmarkProviderState
> {
  public state: BookmarkProviderState = {
    initialized: false,
    add: (vehicleId: string) => this.addToBookmarks(vehicleId),
    remove: (vehicleId: string) => this.removeFromBookmarks(vehicleId),
    bookmarks: [],
    userId: ''
  };
  public addToBookmarks = async (vehicleId: string): Promise<void> => {
    const { userId } = this.state;
    const addResponse = await add({ userId, vehicleId });
    // TODO: get currency
    if (addResponse.success) {
      const vehicleDetailsResponse = await getVehicleDetails({ vehicleId });
      if (vehicleDetailsResponse.success) {
        const { userId, bookmarks } = this.state;

        this.props.trackingApi.trackEvents(addedVehicleToWatchlist(vehicleDetailsResponse.result));

        await this.updateStore({
          userId,
          bookmarks: [...bookmarks, vehicleDetailsResponse.result]
        });
        return;
      } else {
        reportError(
          'BookmarkProvider.addToBookmarks()',
          vehicleDetailsResponse.error
        );
        await remove({ userId, vehicleId });
      }
    } else {
      reportError('BookmarkProvider.addToBookmarks()', addResponse.error);
    }
  };

  public removeFromBookmarks = async (vehicleId: string): Promise<void> => {
    const { userId } = this.state;
    const result = await remove({ userId, vehicleId });
    if (result.success) {
      const { userId, bookmarks } = this.state;
      await this.updateStore({
        userId,
        bookmarks: bookmarks.filter(b => b.vehicleId !== vehicleId)
      });
    } else {
      reportError('BookmarkProvider.removeFromBookmarks()', result.error);
    }
  };

  public async fetchBookmarkStore() {
    const store: Maybe<BookmarkStore> = await offlineStore.tryGetItem(
      BOOKMARKSTORE_KEY
    );
    if (store) {
      this.setState({
        ...store,
        initialized: true
      });
      // update with remote data
      const { userId } = store;
      const remoteBookmarks = await listDetails({ userId });
      if (remoteBookmarks.success) {
        const mappedVehicles = await this.props.typo3.requireVehicleSlugs(remoteBookmarks.result.vehicles);
        await this.updateStore({
          userId,
          bookmarks: mappedVehicles,
        });
      } else {
        reportError(
          'BookmarkProvider.fetchBookmarkStore()',
          remoteBookmarks.error
        );
      }
    } else {
      const generatedStore = generateStore();
      await offlineStore.trySetItem(BOOKMARKSTORE_KEY, generatedStore);
      this.setState({
        ...generatedStore,
        initialized: true
      });
    }
  }

  public async updateStore(newStore: BookmarkStore): Promise<void> {
    await offlineStore.trySetItem(BOOKMARKSTORE_KEY, newStore);
    this.setState({
      ...newStore
    });
  }

  public componentDidMount() {
    // TODO: fetch local bookmarks
    this.fetchBookmarkStore();
  }

  public render() {
    const { children } = this.props;
    return (
      <BookmarkContext.Provider value={this.state}>
        {children}
      </BookmarkContext.Provider>
    );
  }
}

export default withErrorBoundary(withTypo3(withTrackingApi(BookmarkProvider)), 'BookmarkProvider');
